import React, { useState } from 'react';
import {
  Box,
  Typography,
  Link,
} from '@mui/material';
import CommonButton from '../../common/components/CommonButton';
import CommonFormField from '../../common/components/CommonFormField';
import LoadingSpinner from '../../common/components/LoadingSpinner';

const SetPasswordForm = ({ onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    oldpassword: '',
    newpassword: '',
    confirmpassword: ''
  });
  const [errors, setErrors] = useState({});
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validateField = (name, value, allValues = formData) => {
    switch (name) {
      case 'oldpassword':
        if (!value) return 'Current Password is required';
        return '';
      case 'newpassword':
        if (!value) return 'New Password is required';
        if (value.length < 8) return 'Password must be at least 8 characters';
        if (!/[A-Z]/.test(value)) return 'Password must contain at least one uppercase letter';
        if (!/[a-z]/.test(value)) return 'Password must contain at least one lowercase letter';
        if (!/\d/.test(value)) return 'Password must contain at least one number';
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) return 'Password must contain at least one special character';
        return '';
      case 'confirmpassword':
        if (!value) return 'Confirm password is required';
        if (value !== allValues.newpassword) return 'Passwords do not match';
        return '';
      default:
        return '';
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const oldPasswordError = validateField('oldpassword', formData.oldpassword);
    if (oldPasswordError) newErrors.oldpassword = oldPasswordError;

    const passwordError = validateField('newpassword', formData.newpassword);
    if (passwordError) newErrors.newpassword = passwordError;

    const confirmPasswordError = validateField('confirmpassword', formData.confirmpassword);
    if (confirmPasswordError) newErrors.confirmpassword = confirmPasswordError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    const error = validateField(name, value, { ...formData, [name]: value });

    if (error) {
      setErrors(prev => ({ ...prev, [name]: error }));
    } else {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }

    if (name === 'password' && formData.confirmpassword) {
      const confirmError = validateField('confirmpassword', formData.confirmpassword, { ...formData, [name]: value });
      if (confirmError) {
        setErrors(prev => ({ ...prev, confirmpassword: confirmError }));
      } else {
        setErrors(prev => ({ ...prev, confirmpassword: undefined }));
      }
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    onSubmit(formData);
  };

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflowY: 'auto',
      }}
    >
      {loading && (
        <LoadingSpinner />
      )}
      <Box sx={{
        width: '100%',
        maxWidth: {
          sm: '400px',   // Small screens
          md: '400px',
          lg: '500px'
        }
      }}>
        {/* Header */}
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 600, fontSize: '24px' }}>
            Set New Password
          </Typography>
          <Box sx={{ m: '5px 40px' }}>
            <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 400, color: 'text.main' }}>
              Create your new password below and confirm it to continue
            </Typography>
          </Box>
        </Box>

        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 2 }}>
            <CommonFormField
              label="Current Password"
              type="password"
              name="oldpassword"
              value={formData.oldpassword}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="•••••••••"
              error={errors.oldpassword}
              showPassword={showOldPassword}
              onTogglePassword={() => setShowOldPassword(prev => !prev)}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <CommonFormField
              label="Password"
              type="password"
              name="newpassword"
              value={formData.newpassword}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="•••••••••"
              error={errors.newpassword}
              showPassword={showPassword}
              onTogglePassword={() => setShowPassword(prev => !prev)}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <CommonFormField
              label="Confirm Password"
              type="password"
              name="confirmpassword"
              value={formData.confirmpassword}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="•••••••••"
              error={errors.confirmpassword}
              showPassword={showConfirmPassword}
              onTogglePassword={() => setShowConfirmPassword(prev => !prev)}
            />
          </Box>

          <CommonButton
            label="Update"
            size="large"
            type="submit"
            disabled={loading}
          />

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              <Link href="/login" variant="body2" sx={{ textDecoration: 'none', color: 'gray' }}>
                Back to Login
              </Link>
            </Typography>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default SetPasswordForm;